import React, { useState, useEffect } from 'react';
import { 
  getPackageConfig, 
  BookkeeperFrequencyField, 
  PayrollField, 
  EmployeesField,
  BanksField,
  PaymentPlatformsField,
  SalesInvoicesField,
  SuppliersInvoicesField,
  PaymentsFrequencyField,
  BASFrequencyField,
  PAYGFrequencyField,
  ReportsSection,
  SuperannuationField
} from './PackageSpecificForm';

function ClientInfoFormComplete({ formData, onNext, onBack, packageType = 'Starter', packagePrice = 300, oneOffItems = [] }) {
  const packageConfig = getPackageConfig(packageType);
  const isOneOffPackage = packageConfig.is_one_off === true;
  const oneOffTotal = (oneOffItems || []).reduce((sum, it) => sum + (parseFloat(it.amount) || 0), 0);
  const hideSoftwareSection = packageConfig.hide_software_section === true;
  const hideBookkeeperSection = packageConfig.hide_bookkeeper_section === true;
  const hideClientRequirements = packageConfig.hide_client_requirements === true;
  
  const [data, setData] = useState({
    // Client Details
    business_name: formData.business_name || '',
    business_owners: formData.business_owners || '',
    abn: formData.abn || '',
    business_type: formData.business_type || '',
    
    // Contact Details
    main_contact: formData.main_contact || '',
    phone: formData.phone || '',
    email: formData.email || '',
    website: formData.website || '',
    address: formData.address || '',
    
    // Accountant
    accountant: formData.accountant || '',
    
    // Software
    software_subscription: formData.software_subscription || 'Xero',
    software_managed_by: formData.software_managed_by || 'Client',
    
    // Bookkeeper Requirements
    bookkeeper_frequency: formData.bookkeeper_frequency || 'Monthly',
    
    // Bookkeeper Tasks
    sales_invoices_frequency: formData.sales_invoices_frequency || '',
    supplier_invoices_frequency: formData.supplier_invoices_frequency || '',
    payments_frequency: formData.payments_frequency || '',
    payroll_frequency: formData.payroll_frequency || '',
    num_employees: formData.num_employees || '',
    num_banks: formData.num_banks || '',
    num_payment_platforms: formData.num_payment_platforms || '',
    banking_other: formData.banking_other || '',
    other_tasks: formData.other_tasks || '',
    superannuation_submission: formData.superannuation_submission || 'Quarterly',
    
    // Client Registration Details
    gst_registration: formData.gst_registration || 'Accrual',
    bas_frequency: formData.bas_frequency || 'Quarterly',
    payg_frequency: formData.payg_frequency || '',
    paygi_instalment: formData.paygi_instalment || '',
    
    // Client Requirements
    monthly_reports: formData.monthly_reports || '',
    annual_reports: formData.annual_reports || 'Yes',
    additional_tasks: formData.additional_tasks || '',
    
    // Other Notes
    leases: formData.leases || ''
  });

  const [errors, setErrors] = useState({});
  
  // Set package-specific defaults on mount
  useEffect(() => {
    const config = getPackageConfig(packageType);
    const updates = {};
    
    // Set all hard-coded values
    if (config.bookkeeper_frequency_hardcoded) updates.bookkeeper_frequency = config.bookkeeper_frequency_hardcoded;
    if (config.sales_invoices_hardcoded) updates.sales_invoices_frequency = config.sales_invoices_hardcoded;
    if (config.suppliers_invoices_hardcoded) updates.supplier_invoices_frequency = config.suppliers_invoices_hardcoded;
    if (config.payments_hardcoded) updates.payments_frequency = config.payments_hardcoded;
    if (config.payroll_hardcoded) updates.payroll_frequency = config.payroll_hardcoded;
    if (config.num_banks_hardcoded) updates.num_banks = config.num_banks_hardcoded;
    if (config.num_payment_platforms_hardcoded) updates.num_payment_platforms = config.num_payment_platforms_hardcoded;
    if (config.num_employees_hardcoded) updates.num_employees = config.num_employees_hardcoded;
    if (config.bas_frequency_hardcoded) updates.bas_frequency = config.bas_frequency_hardcoded;
    if (config.payg_frequency_hardcoded) updates.payg_frequency = config.payg_frequency_hardcoded;
    if (config.superannuation_hardcoded) updates.superannuation_submission = config.superannuation_hardcoded;
    
    if (Object.keys(updates).length > 0) {
      setData(prev => ({ ...prev, ...updates }));
    }
  }, [packageType]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!data.business_name) newErrors.business_name = 'Business name is required';
    if (!data.abn || data.abn.length < 11) newErrors.abn = 'Valid ABN is required (11 digits)';
    if (!data.main_contact) newErrors.main_contact = 'Main contact is required';
    if (!data.email || !/\S+@\S+\.\S+/.test(data.email)) {
      newErrors.email = 'Valid email is required';
    }
    if (!data.phone) newErrors.phone = 'Phone number is required';
    
    setErrors(newErrors);
    
    // Show alert if validation fails
    if (Object.keys(newErrors).length > 0) {
      alert('Please fill in all required fields:\n' + Object.values(newErrors).join('\n'));
      // Scroll to top to see errors
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      // Structure data correctly for document generator
      const structuredData = {
        business_name: data.business_name,
        abn: data.abn,
        business_type: data.business_type,
        entity_type: 'Company', // Default for now
        directors: data.business_owners ? data.business_owners.split(',').map(d => d.trim()) : [],
        
        // Contact details - nested structure for document generator
        contacts: {
          name: data.main_contact,
          email: data.email,
          phone: data.phone,
          address: data.address,
          website: data.website
        },
        
        // Software - nested structure
        software: {
          subscription: data.software_subscription,
          managed_by: data.software_managed_by
        },
        
        // Services - nested structure
        services: {
          bookkeeper_frequency: data.bookkeeper_frequency,
          sales_invoices_frequency: data.sales_invoices_frequency,
          supplier_invoices_frequency: data.supplier_invoices_frequency,
          payments_frequency: data.payments_frequency,
          payroll: data.payroll_frequency ? true : false,
          payroll_frequency: data.payroll_frequency,
          num_employees: data.num_employees,
          banking: {
            num_banks: data.num_banks,
            num_payment_platforms: data.num_payment_platforms,
            other: data.banking_other
          },
          other_tasks: data.other_tasks
        },
        
        // Compliance - nested structure
        compliance: {
          gst_registered: true,
          gst_method: data.gst_registration,
          bas_frequency: data.bas_frequency,
          paygi_instalment: data.paygi_instalment
        },
        
        // Pricing - nested structure (from admin-selected package)
        pricing: {
          monthly_total: packagePrice?.toString() || '0.00',
          software_component: '50.00',
          package: packageType || 'Starter',
          one_off_items: oneOffItems || [],
          is_one_off: isOneOffPackage
        },
        
        // Tax agent - nested structure
        tax_agent: {
          has_agent: data.accountant ? true : false,
          name: data.accountant || '',
          contact: ''
        },
        
        // Additional info
        client_requirements: {
          monthly_reports: data.monthly_reports,
          annual_reports: data.annual_reports,
          additional_tasks: data.additional_tasks
        },
        
        other_notes: {
          leases: data.leases
        },
        
        // Keep raw form data for reference
        _raw_form_data: data
      };
      
      onNext(structuredData);
    }
  };

  return (
    <div className="card fade-in">
      <h2>New Client Information Sheet</h2>
      <div style={{ 
        backgroundColor: 'var(--bg-gray-100)', 
        padding: '1rem', 
        borderRadius: '0.5rem',
        marginBottom: '1rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>Selected Package</div>
          <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--primary-navy)' }}>{packageType}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>
            {isOneOffPackage ? 'One-Off Investment' : 'Monthly Investment'}
          </div>
          <div style={{ fontSize: '1.25rem', fontWeight: 'bold', color: 'var(--success-green)' }}>
            {isOneOffPackage ? `$${oneOffTotal.toFixed(2)} One-off` : `$${packagePrice}/mo`}
          </div>
        </div>
      </div>
      <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>
        Please complete all sections. Fields marked with * are required.
      </p>

      <form onSubmit={handleSubmit} data-testid="client-info-form">
        
        {/* CLIENT DETAILS */}
        <h3 style={{ marginTop: '2rem' }}>Client Details</h3>
        <hr style={{ margin: '1rem 0', borderTop: '2px solid var(--primary-navy)' }} />
        
        <div className="form-group">
          <label htmlFor="business_name">Business Name *</label>
          <input
            type="text"
            id="business_name"
            data-testid="business-name"
            value={data.business_name}
            onChange={(e) => setData({ ...data, business_name: e.target.value })}
            className={errors.business_name ? 'error' : ''}
          />
          {errors.business_name && <p className="error-message">{errors.business_name}</p>}
        </div>

        <div className="form-group">
          <label htmlFor="business_owners">Business Owners / Directors</label>
          <input
            type="text"
            id="business_owners"
            value={data.business_owners}
            onChange={(e) => setData({ ...data, business_owners: e.target.value })}
            placeholder="e.g., John Smith, Jane Doe"
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <div className="form-group">
            <label htmlFor="abn">Business ABN *</label>
            <input
              type="text"
              id="abn"
              value={data.abn}
              onChange={(e) => setData({ ...data, abn: e.target.value.replace(/\s/g, '') })}
              maxLength="11"
              placeholder="12345678901"
              className={errors.abn ? 'error' : ''}
            />
            {errors.abn && <p className="error-message">{errors.abn}</p>}
          </div>

          <div className="form-group">
            <label htmlFor="business_type">Type of Business / Industry</label>
            <input
              type="text"
              id="business_type"
              value={data.business_type}
              onChange={(e) => setData({ ...data, business_type: e.target.value })}
              placeholder="e.g., Construction, Retail, IT Services"
            />
          </div>
        </div>

        {/* CONTACT DETAILS */}
        <h3 style={{ marginTop: '2rem' }}>Contact Details</h3>
        <hr style={{ margin: '1rem 0', borderTop: '2px solid var(--primary-navy)' }} />

        <div className="form-group">
          <label htmlFor="main_contact">Main Contact *</label>
          <input
            type="text"
            id="main_contact"
            value={data.main_contact}
            onChange={(e) => setData({ ...data, main_contact: e.target.value })}
            className={errors.main_contact ? 'error' : ''}
          />
          {errors.main_contact && <p className="error-message">{errors.main_contact}</p>}
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <div className="form-group">
            <label htmlFor="phone">Phone *</label>
            <input
              type="tel"
              id="phone"
              value={data.phone}
              onChange={(e) => setData({ ...data, phone: e.target.value })}
              className={errors.phone ? 'error' : ''}
            />
            {errors.phone && <p className="error-message">{errors.phone}</p>}
          </div>

          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              value={data.email}
              onChange={(e) => setData({ ...data, email: e.target.value })}
              className={errors.email ? 'error' : ''}
            />
            {errors.email && <p className="error-message">{errors.email}</p>}
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="website">Website</label>
          <input
            type="text"
            id="website"
            value={data.website}
            onChange={(e) => setData({ ...data, website: e.target.value })}
            placeholder="www.example.com"
          />
        </div>

        <div className="form-group">
          <label htmlFor="address">Address</label>
          <textarea
            id="address"
            value={data.address}
            onChange={(e) => setData({ ...data, address: e.target.value })}
            rows="3"
          />
        </div>

        {/* ACCOUNTANT */}
        <h3 style={{ marginTop: '2rem' }}>Accountant</h3>
        <hr style={{ margin: '1rem 0', borderTop: '2px solid var(--primary-navy)' }} />
        
        <div className="form-group">
          <label htmlFor="accountant">Accountant Name & Contact</label>
          <input
            type="text"
            id="accountant"
            value={data.accountant}
            onChange={(e) => setData({ ...data, accountant: e.target.value })}
            placeholder="Accountant name, phone, email"
          />
        </div>

        {/* SOFTWARE - hidden for Start-up Pack (they don't have software yet) */}
        {!hideSoftwareSection && (
        <>
        <h3 style={{ marginTop: '2rem' }}>Software Used</h3>
        <hr style={{ margin: '1rem 0', borderTop: '2px solid var(--primary-navy)' }} />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <div className="form-group">
            <label htmlFor="software_subscription">Software Subscription</label>
            <select
              id="software_subscription"
              value={data.software_subscription}
              onChange={(e) => setData({ ...data, software_subscription: e.target.value })}
            >
              <option value="Xero">Xero</option>
              <option value="Quickbooks">Quickbooks</option>
              <option value="Other">Other</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="software_managed_by">Managed By</label>
            <select
              id="software_managed_by"
              value={data.software_managed_by}
              onChange={(e) => setData({ ...data, software_managed_by: e.target.value })}
            >
              <option value="Client">Client</option>
              <option value="Accountant">Accountant</option>
              <option value="Other">Other</option>
            </select>
          </div>
        </div>
        </>
        )}

        {/* BOOKKEEPER REQUIREMENTS - hidden for BAS Only and one-off packages */}
        {packageType !== 'BAS Only' && !hideBookkeeperSection && (
        <>
        <h3 style={{ marginTop: '2rem' }}>Bookkeeper Requirements</h3>
        <hr style={{ margin: '1rem 0', borderTop: '2px solid var(--primary-navy)' }} />

        <BookkeeperFrequencyField
          packageType={packageType}
          value={data.bookkeeper_frequency}
          onChange={(value) => setData({ ...data, bookkeeper_frequency: value })}
          errors={errors}
        />

        <h4 style={{ marginTop: '1.5rem', fontSize: '1rem' }}>Bookkeeper Tasks</h4>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <SalesInvoicesField
            packageType={packageType}
            value={data.sales_invoices_frequency}
            onChange={(value) => setData({ ...data, sales_invoices_frequency: value })}
          />

          <SuppliersInvoicesField
            packageType={packageType}
            value={data.supplier_invoices_frequency}
            onChange={(value) => setData({ ...data, supplier_invoices_frequency: value })}
          />
        </div>

        <PaymentsFrequencyField
          packageType={packageType}
          value={data.payments_frequency}
          onChange={(value) => setData({ ...data, payments_frequency: value })}
        />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <PayrollField
            packageType={packageType}
            value={data.payroll_frequency}
            onChange={(value) => setData({ ...data, payroll_frequency: value })}
          />

          <EmployeesField
            packageType={packageType}
            value={data.num_employees}
            onChange={(value) => setData({ ...data, num_employees: value })}
          />
        </div>

        <h4 style={{ marginTop: '1.5rem', fontSize: '1rem' }}>Banking, Feeds, Reconciliations</h4>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem' }}>
          <BanksField
            packageType={packageType}
            value={data.num_banks}
            onChange={(value) => setData({ ...data, num_banks: value })}
          />

          <PaymentPlatformsField
            packageType={packageType}
            value={data.num_payment_platforms}
            onChange={(value) => setData({ ...data, num_payment_platforms: value })}
          />
        </div>

        <div className="form-group">
          <label htmlFor="other_tasks">Any additional tasks</label>
          <input
            type="text"
            id="other_tasks"
            value={data.other_tasks}
            onChange={(e) => setData({ ...data, other_tasks: e.target.value })}
            placeholder="Enter any additional requirements"
          />
        </div>
        </>
        )}

        {/* CLIENT REGISTRATION DETAILS - hidden for Starter (not BAS registered) */}
        {(packageType !== 'Starter' || isOneOffPackage) && (
          <>
        <h3 style={{ marginTop: '2rem' }}>BAS Registration Details</h3>
        <hr style={{ margin: '1rem 0', borderTop: '2px solid var(--primary-navy)' }} />

          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <div className="form-group">
            <label htmlFor="gst_registration">GST Registration</label>
            <select
              id="gst_registration"
              data-testid="gst-registration-select"
              value={data.gst_registration}
              onChange={(e) => setData({ ...data, gst_registration: e.target.value })}
            >
              <option value="Cash">Cash</option>
              <option value="Accrual">Accrual</option>
              {isOneOffPackage && <option value="Not Applicable">Not Applicable</option>}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="bas_frequency">BAS Frequency</label>
            {data.gst_registration === 'Not Applicable' ? (
              <input
                type="text"
                id="bas_frequency"
                value="Not Applicable"
                disabled
                data-testid="bas-frequency-disabled"
                style={{ backgroundColor: 'var(--bg-gray-100)', cursor: 'not-allowed' }}
              />
            ) : (
              <BASFrequencyField
                packageType={packageType}
                value={data.bas_frequency}
                onChange={(value) => setData({ ...data, bas_frequency: value })}
              />
            )}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem' }}>
          <div className="form-group">
            <label htmlFor="payg_frequency">PAYG Frequency</label>
            {data.gst_registration === 'Not Applicable' ? (
              <input
                type="text"
                id="payg_frequency"
                value="Not Applicable"
                disabled
                data-testid="payg-frequency-disabled"
                style={{ backgroundColor: 'var(--bg-gray-100)', cursor: 'not-allowed' }}
              />
            ) : (
              <PAYGFrequencyField
                packageType={packageType}
                value={data.payg_frequency}
                onChange={(value) => setData({ ...data, payg_frequency: value })}
              />
            )}
          </div>

          <div className="form-group">
            <label htmlFor="paygi_instalment">PAYGI Instalment</label>
            <input
              type="text"
              id="paygi_instalment"
              value={data.gst_registration === 'Not Applicable' ? 'Not Applicable' : data.paygi_instalment}
              onChange={(e) => setData({ ...data, paygi_instalment: e.target.value })}
              placeholder="Yes - Rate or Amount"
              disabled={data.gst_registration === 'Not Applicable'}
              style={data.gst_registration === 'Not Applicable' ? { backgroundColor: 'var(--bg-gray-100)', cursor: 'not-allowed' } : {}}
            />
          </div>
        </div>
        
        {packageType !== 'Starter' && !isOneOffPackage && (
          <SuperannuationField
            packageType={packageType}
            value={data.superannuation_submission || 'Quarterly'}
            onChange={(value) => setData({ ...data, superannuation_submission: value })}
          />
        )}
          </>
          </>
        )}

        {/* CLIENT REQUIREMENTS - Package Specific (hidden for one-off packages) */}
        {!hideClientRequirements && (
        <>
        <ReportsSection
          packageType={packageType}
          monthlyReports={data.monthly_reports}
          annualReports={data.annual_reports}
          onChange={(field, value) => setData({ ...data, [field]: value })}
        />

        <div className="form-group">
          <label htmlFor="additional_tasks">Additional Tasks</label>
          <textarea
            id="additional_tasks"
            value={data.additional_tasks}
            onChange={(e) => setData({ ...data, additional_tasks: e.target.value })}
            rows="2"
            placeholder="Any specific additional work required beyond standard package"
          />
        </div>
        </>
        )}

        {/* Navigation */}
        <div style={{ display: 'flex', gap: '1rem', marginTop: '2rem' }}>
          <button 
            type="button" 
            className="btn btn-outline"
            data-testid="back-button"
            onClick={onBack}
          >
            ← Back
          </button>
          <button 
            type="submit" 
            className="btn btn-primary"
            data-testid="next-button"
            style={{ flex: 1 }}
          >
            Continue to Documents →
          </button>
        </div>
      </form>
    </div>
  );
}

export default ClientInfoFormComplete;
